/**
 * Single source of truth for severity and workflow-status vocabulary.
 *
 * Severity answers "how bad is it" and is drawn with the --state-* color
 * tokens (filled tint + colored text + border). Status answers "where is it
 * in my workflow" and is drawn neutrally (outlined, with an icon), so the two
 * never read as the same thing. Every badge prints its label, so meaning is
 * never carried by color alone.
 *
 * Use <SeverityBadge> / <StatusBadge> from components/primitives where you can;
 * use these helpers when you need the raw tokens (charts, rails, dots).
 */

const tone = (name) => ({
  color: `var(--state-${name})`,
  bg: `var(--state-${name}-bg)`,
  border: `var(--state-${name}-border)`,
});

export const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];

export const SEVERITY = {
  Critical: { key: 'critical', label: 'Critical', short: 'CRIT', rank: 4, ...tone('critical') },
  High:     { key: 'high',     label: 'High',     short: 'HIGH', rank: 3, ...tone('high') },
  Medium:   { key: 'medium',   label: 'Medium',   short: 'MED',  rank: 2, ...tone('medium') },
  Low:      { key: 'low',      label: 'Low',      short: 'LOW',  rank: 1, ...tone('low') },
  Info:     { key: 'info',     label: 'Info',     short: 'INFO', rank: 0, ...tone('info') },
};

/** Accepts 'critical', 'CRITICAL', 'Critical', 'crit', 'med', 'informational', null... */
export function normalizeSeverity(value) {
  const v = String(value || '').trim().toLowerCase();
  if (v.startsWith('crit')) return 'Critical';
  if (v.startsWith('high')) return 'High';
  if (v.startsWith('med') || v === 'moderate') return 'Medium';
  if (v.startsWith('low')) return 'Low';
  return 'Info';
}

/** Token triple + label for any severity-ish value. Unknown values map to Info. */
export function severityMeta(value) {
  return SEVERITY[normalizeSeverity(value)];
}

/** Sort comparator: most severe first. */
export function bySeverityDesc(a, b) {
  return severityMeta(b).rank - severityMeta(a).rank;
}

/*
 * Brief workflow statuses (Inbox). Neutral styling; 'new' gets the accent so
 * unread items stand out without borrowing a severity color.
 */
export const STATUSES = ['new', 'triage', 'acked', 'shipped', 'archived'];

export const STATUS = {
  new:      { key: 'new',      label: 'New',          color: 'var(--accent-from)',    border: 'var(--border-accent)',  bg: 'var(--accent-muted)' },
  triage:   { key: 'triage',   label: 'In triage',    color: 'var(--text-primary)',   border: 'var(--border-strong)',  bg: 'transparent' },
  acked:    { key: 'acked',    label: 'Acknowledged', color: 'var(--text-secondary)', border: 'var(--border-default)', bg: 'transparent' },
  archived: { key: 'archived', label: 'Archived',     color: 'var(--text-muted)',     border: 'var(--border-default)', bg: 'transparent' },
  shipped:  { key: 'shipped',  label: 'Rules shipped', color: 'var(--state-success)',  border: 'var(--state-success-border)', bg: 'transparent' },
};

export function statusMeta(value) {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'unread') return STATUS.new;   // useBriefStatus calls 'new' 'unread'
  if (v === 'ack' || v === 'acknowledged') return STATUS.acked;
  if (v === 'in_triage' || v === 'in triage' || v === 'triaging') return STATUS.triage;
  return STATUS[v] || STATUS.new;
}
